'use client'

import { PanelLeftClose, PanelRight } from 'lucide-react'
import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'
import { FieldValues } from 'react-hook-form'

import { TabFiltersProps } from '@components/Filters/filter.types'
import { FilterForm } from '@components/Filters/FilterForm'
import { APP_HOTKEYS_LS_KEY } from '@features/appHotkeys/appHotkeys'
import { Button, Kbd } from '@fp/ui-kit'
import { useLocalStorage } from '@hooks/useLocalStorage'
import { cn } from '@lib/cn'
import { createSearchParamsBrackets } from '@lib/createSearchParamsBrackets'
import { emitToast, shortBusProps } from '@lib/emitToast'
import { iconStroke } from '@lib/iconProps'

const SIDEBAR_KEYBOARD_SHORTCUT = 'b'

const SIDEBAR_STATE_LS_KEY = 'sidebarState'

export const Filters = <T extends FieldValues, Q>({ filtersConfig }: TabFiltersProps<T, Q>) => {
  const { valueLS: isAppHotkeysEnabled } = useLocalStorage(APP_HOTKEYS_LS_KEY, false)
  const pathname = usePathname()
  const { replace } = useRouter()
  const searchParams = useSearchParams()

  const { valueLS: isCollapsed, setValueLS: setIsCollapsed } = useLocalStorage(SIDEBAR_STATE_LS_KEY, false)

  const onToggleSidebar = useCallback(() => {
    setIsCollapsed(!isCollapsed)
  }, [isCollapsed, setIsCollapsed])

  const onFinish = useCallback(
    (data: T) => {
      const newParams = createSearchParamsBrackets(data)
      const newQuery = newParams.toString()

      const currentQuery = searchParams.toString()

      if (newQuery === currentQuery) {
        return
      }

      const hash = typeof window !== 'undefined' ? window.location.hash : ''
      replace(newQuery ? `${pathname}?${newQuery}${hash}` : `${pathname}${hash}`, { scroll: false })
    },
    [pathname, replace, searchParams],
  )

  // Adds a keyboard shortcut to toggle the sidebar.
  useEffect(() => {
    if (!isAppHotkeysEnabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onToggleSidebar()
        emitToast({
          ...shortBusProps,
          type: 'info',
          icon: <Kbd size='sm'>{SIDEBAR_KEYBOARD_SHORTCUT.toUpperCase()}</Kbd>,
          message: `Sidebar toggled (CMD+${SIDEBAR_KEYBOARD_SHORTCUT.toUpperCase()}).`,
        })
      }
    }
    window.addEventListener('keydown', handleKeyDown)

    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onToggleSidebar, isAppHotkeysEnabled])

  const resetFilters = useCallback(() => {
    const params = new URLSearchParams()
    const hash = typeof window !== 'undefined' ? window.location.hash : ''
    replace(`${pathname}?${params.toString()}${hash}`, { scroll: false })
  }, [pathname, replace])

  return (
    <div
      className={cn(
        'top-20 sticky h-full max-h-[calc(100vh-80px)] transform-gpu',
        isCollapsed ? 'w-8! min-w-8! md:w-18! md:min-w-18!' : 'w-75 animate-fade-in',
      )}
    >
      <div className='flex flex-col h-full border-r border-input transition-all'>
        <div
          className={`${isCollapsed ? 'hidden w-0' : 'flex pt-4 w-full'} flex-1 flex-col px-5 overflow-auto transform-gpu `}
        >
          <h3 className='text-lg font-semibold pb-4'>Filters</h3>

          <FilterForm
            filtersConfig={filtersConfig}
            onFinish={onFinish}
            resetFilters={resetFilters}
            pathname={pathname}
          />
        </div>

        <div className={`${isCollapsed ? 'h-full py-1 px-0.5' : 'py-2 px-5 border-t border-input'}`}>
          <Button
            variant={isCollapsed ? 'ghost' : 'ghostOutline'}
            size={isCollapsed ? 'icon' : 'sm'}
            className={`w-full ${isCollapsed && 'items-end h-full pb-2'}`}
            onClick={onToggleSidebar}
          >
            {isCollapsed ? (
              <PanelRight />
            ) : (
              <span className='flex items-center gap-1'>
                <PanelLeftClose className='size-1!' strokeWidth={iconStroke.thin} />
                Hide sidebar {isAppHotkeysEnabled && <Kbd size='xs'>CMD+B</Kbd>}
              </span>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
